import type { AuditTrailItem, AuditTrailModule } from './auditTrailTypes';
import { formatAuditTrailDateTime } from '../../../../audit/auditTrailDateTime';

export { formatAuditTrailDateTime };

export const AUDIT_MODULES: { value: AuditTrailModule; label: string }[] = [
  { value: 'PROJECT', label: 'Project' },
  { value: 'PIPE_DETAILS', label: 'Pipe details' },
  { value: 'PIPE_DOCUMENTS', label: 'Pipe documents' },
  { value: 'PIPE_MATERIAL_REPORTS', label: 'Pipe mill reports' },
  { value: 'REGISTRY', label: 'Registry' },
  { value: 'REGISTRY_CONSUMABLE', label: 'Registry consumable' },
  { value: 'REGISTRY_MISC', label: 'Registry miscellaneous' },
  { value: 'TRACEABILITY', label: 'Material traceability' },
  { value: 'PIPE_INVENTORY', label: 'Pipe inventory (analysis)' },
  { value: 'COATING', label: 'Coating' },
  { value: 'PRODUCTIONS', label: 'Production' },
  { value: 'SPOOL', label: 'Spool tracker' },
  { value: 'PRODUCTION', label: 'Production (legacy)' },
  { value: 'REPORTS', label: 'Reports' },
  { value: 'TEAM', label: 'Team members' },
  { value: 'USER', label: 'User management' },
  { value: 'USER_CERT', label: 'User certificates' },
  { value: 'TAGS', label: 'Tags' },
  { value: 'USER_TABLE_LAYOUT', label: 'Table layout' },
  { value: 'COMPANY', label: 'Company' },
  { value: 'DEPARTMENT', label: 'Department' },
  { value: 'LOCATION', label: 'Location' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'CONTRACTOR', label: 'Contractor' },
  { value: 'MANUFACTURER', label: 'Manufacturer' },
  { value: 'SYSTEM_SETTINGS', label: 'System settings' },
  { value: 'ROLE_APP', label: 'App roles' },
  { value: 'ROLE_PROJECT', label: 'Project roles' },
  { value: 'NTD_LOG', label: 'NDT log' },
  { value: 'NTD_LOG_DOC', label: 'NDT log documents' },
  { value: 'INSPECTION_PERSONNEL', label: 'Inspection personnel' },
  { value: 'WELDER_QUAL', label: 'Welder qualification' },
  { value: 'ARK_REGISTRY', label: 'Ark registry' },
  { value: 'COGNITO', label: 'Authentication' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'ACTIVITY', label: 'User activity (UI)' },
  { value: 'DEVICE', label: 'Device management' },
  { value: 'SPECIFICATIONS', label: 'Specifications' },
  { value: 'SETUP', label: 'Setup' },
];

export const MODULE_LABELS: Record<string, string> = Object.fromEntries(
  AUDIT_MODULES.map((m) => [m.value, m.label])
);

export const SEVERITY_OPTIONS = [
  { value: 'INFO', label: 'Info' },
  { value: 'WARN', label: 'Warning' },
  { value: 'ERR', label: 'Error' },
];

export const SEVERITY_LABELS: Record<string, string> = {
  INFO: 'Info',
  WARN: 'Warning',
  WARNING: 'Warning',
  ERR: 'Error',
  ERROR: 'Error',
};

/** event_type → label; optional module hint for filtering */
export const AUDIT_EVENTS: { value: string; label: string; module?: AuditTrailModule }[] = [
  { value: 'PROJECT_CREATED', label: 'Project created', module: 'PROJECT' },
  { value: 'PROJECT_UPDATED', label: 'Project updated', module: 'PROJECT' },
  { value: 'PROJECT_DELETED', label: 'Project deleted', module: 'PROJECT' },
  { value: 'PROJECT_BULK_DELETED', label: 'Projects deleted', module: 'PROJECT' },
  { value: 'PROJECT_BULK_SOFT_DELETED', label: 'Projects disabled', module: 'PROJECT' },
  { value: 'PIPE_DETAILS_BULK_UPSERT', label: 'Pipe details  upsert', module: 'PIPE_DETAILS' },
  { value: 'PIPE_DETAILS_BULK_DELETED', label: 'Pipe details  deleted', module: 'PIPE_DETAILS' },
  { value: 'PIPE_DOCUMENT_CREATED', label: 'Pipe document created', module: 'PIPE_DOCUMENTS' },
  { value: 'PIPE_DOCUMENT_UPDATED', label: 'Pipe document updated', module: 'PIPE_DOCUMENTS' },
  { value: 'PIPE_DOCUMENT_BULK_DELETED', label: 'Pipe documents deleted', module: 'PIPE_DOCUMENTS' },
  { value: 'PIPE_DOCUMENTS_DELETED_BY_PIPE', label: 'Pipe documents deleted by pipe', module: 'PIPE_DOCUMENTS' },
  { value: 'PIPE_MATERIAL_REPORT_CREATED', label: 'Pipe mill report created', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'PIPE_MATERIAL_REPORT_UPDATED', label: 'Pipe mill report updated', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'PIPE_MATERIAL_REPORT_DELETED', label: 'Pipe mill report deleted', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'PIPE_MILL_REPORT_METADATA_CREATED', label: 'Pipe mill metadata created', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'PIPE_MILL_REPORT_METADATA_UPDATED', label: 'Pipe mill metadata updated', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'PIPE_MATERIAL_REPORT_BULK_SAVED', label: 'Pipe mill reports bulk saved', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'PIPE_MATERIAL_REPORT_STATUS_UPDATED', label: 'Pipe mill report status updated', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'PIPE_MATERIAL_REPORT_ITEM_SAVED', label: 'Pipe mill report item saved', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'PIPE_MATERIAL_REPORT_ITEM_DELETED', label: 'Pipe mill report item deleted', module: 'PIPE_MATERIAL_REPORTS' },
  { value: 'DOCUMENT_UPLOADED', label: 'Document uploaded', module: 'PIPE_DOCUMENTS' },
  { value: 'REGISTRY_BULK_UPSERT', label: 'Registry items saved', module: 'REGISTRY' },
  { value: 'REGISTRY_BULK_DELETED', label: 'Registry items deleted', module: 'REGISTRY' },
  { value: 'REGISTRY_DOCUMENT_CREATED', label: 'Registry document created', module: 'REGISTRY' },
  { value: 'REGISTRY_DOCUMENT_UPDATED', label: 'Registry document updated', module: 'REGISTRY' },
  { value: 'REGISTRY_DOCUMENTS_BULK_DELETED', label: 'Registry documents deleted', module: 'REGISTRY' },
  { value: 'REGISTRY_REPORT_APPROVAL_STATUS_BULK_UPDATED', label: 'Registry report approvals updated', module: 'REGISTRY' },
  { value: 'REGISTRY_CONSUMABLE_BULK_UPSERT', label: 'Registry consumables saved', module: 'REGISTRY_CONSUMABLE' },
  { value: 'REGISTRY_CONSUMABLE_BULK_DELETED', label: 'Registry consumables deleted', module: 'REGISTRY_CONSUMABLE' },
  { value: 'REGISTRY_CONSUMABLE_DOCUMENTS_BULK_DELETED', label: 'Registry consumable documents deleted', module: 'REGISTRY_CONSUMABLE' },
  { value: 'REGISTRY_MISC_BULK_UPSERT', label: 'Registry miscellaneous items saved', module: 'REGISTRY_MISC' },
  { value: 'REGISTRY_MISC_BULK_DELETED', label: 'Registry miscellaneous items deleted', module: 'REGISTRY_MISC' },
  { value: 'REGISTRY_MISC_DOCUMENTS_BULK_DELETED', label: 'Registry miscellaneous documents deleted', module: 'REGISTRY_MISC' },
  { value: 'MATERIAL_TRACEABILITY_CREATED', label: 'Material traceability created', module: 'TRACEABILITY' },
  { value: 'MATERIAL_TRACEABILITY_BULK_UPDATED', label: 'Material traceability bulk updated', module: 'TRACEABILITY' },
  { value: 'MATERIAL_TRACEABILITY_BULK_DELETED', label: 'Material traceability deleted', module: 'TRACEABILITY' },
  { value: 'MATERIAL_TRACEABILITY_DOCS_BULK_DELETED', label: 'Traceability documents deleted', module: 'TRACEABILITY' },
  {
    value: 'PIPE_INVENTORY_MATERIAL_ADDED',
    label: 'Pipe inventory material added',
    module: 'PIPE_INVENTORY',
  },
  {
    value: 'PIPE_INVENTORY_MATERIAL_UPDATED',
    label: 'Pipe inventory material updated',
    module: 'PIPE_INVENTORY',
  },
  {
    value: 'PIPE_INVENTORY_MATERIAL_DELETED',
    label: 'Pipe inventory material deleted',
    module: 'PIPE_INVENTORY',
  },
  { value: 'COATING_UPLOAD_LOGGED', label: 'Coating upload logged', module: 'COATING' },
  { value: 'PRODUCTION_PUMP_HOUSE_COATING_UPLOADED', label: 'Pump house coating uploaded', module: 'COATING' },
  { value: 'TEAM_MEMBER_CREATED', label: 'Team member created', module: 'TEAM' },
  { value: 'TEAM_MEMBER_UPDATED', label: 'Team member updated', module: 'TEAM' },
  { value: 'USER_CREATED', label: 'User created', module: 'USER' },
  { value: 'USER_UPDATED', label: 'User updated', module: 'USER' },
  { value: 'USER_BULK_UPDATED', label: 'Users updated', module: 'USER' },
  { value: 'USER_DELETED', label: 'User deleted', module: 'USER' },
  { value: 'USER_BULK_DELETED', label: 'Users deleted', module: 'USER' },
  { value: 'USER_ROLE_CHANGED', label: 'User role changed', module: 'USER' },
  { value: 'USER_ENABLED', label: 'User enabled', module: 'USER' },
  { value: 'USER_DISABLED', label: 'User disabled', module: 'USER' },
  { value: 'USER_BULK_ENABLED', label: 'Users enabled', module: 'USER' },
  { value: 'USER_BULK_DISABLED', label: 'Users disabled', module: 'USER' },
  { value: 'AUTH_LOGIN_SUCCESS', label: 'User login', module: 'COGNITO' },
  { value: 'AUTH_LOGIN_FAILED', label: 'Login failed', module: 'COGNITO' },
  { value: 'AUTH_LOGOUT', label: 'User logout', module: 'COGNITO' },
  { value: 'ACCESS_DENIED', label: 'Access denied', module: 'SECURITY' },
  { value: 'ACTIVITY_TAB_VIEW', label: 'Tab opened', module: 'ACTIVITY' },
  { value: 'ACTIVITY_PAGE_VIEW', label: 'Page opened', module: 'ACTIVITY' },
  { value: 'ACTIVITY_SEARCH', label: 'Search performed', module: 'ACTIVITY' },
  { value: 'ACTIVITY_FILTER_APPLIED', label: 'Filter applied', module: 'ACTIVITY' },
  { value: 'ACTIVITY_FILTER_CLEARED', label: 'Filters cleared', module: 'ACTIVITY' },
  { value: 'ACTIVITY_EXPORT', label: 'Export / download', module: 'ACTIVITY' },
  { value: 'AUTH_USER_PASSWORD_RESET', label: 'User password reset', module: 'COGNITO' },
  { value: 'COGNITO_USER_CREATED', label: 'Cognito user created', module: 'COGNITO' },
  { value: 'COGNITO_USER_DELETED', label: 'Cognito user deleted', module: 'COGNITO' },
  { value: 'COGNITO_USER_ENABLED', label: 'Cognito user enabled', module: 'COGNITO' },
  { value: 'COGNITO_ADMIN_SET_PASSWORD', label: 'Admin set user password', module: 'COGNITO' },
  { value: 'COGNITO_LOGIN_DETAILS_EMAIL_SENT', label: 'Login details email sent', module: 'COGNITO' },
  { value: 'TAG_CREATED', label: 'Tag created', module: 'TAGS' },
  { value: 'USER_TABLE_LAYOUT_UPSERT', label: 'Table layout saved', module: 'USER_TABLE_LAYOUT' },
  { value: 'COMPANY_CREATED', label: 'Company created', module: 'COMPANY' },
  { value: 'COMPANY_UPDATED', label: 'Company updated', module: 'COMPANY' },
  { value: 'WELD_STARTED', label: 'Weld started', module: 'PRODUCTIONS' },
  { value: 'WELD_UPDATED', label: 'Weld updated', module: 'PRODUCTIONS' },
  { value: 'WELD_DELETED', label: 'Weld deleted', module: 'PRODUCTIONS' },
  { value: 'WELD_COMPLETED', label: 'Weld completed', module: 'PRODUCTIONS' },
  { value: 'WELD_VISUAL_INSPECTION_UPDATED', label: 'Visual inspection updated', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_BASE_UPDATED', label: 'Production base updated', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_DELETED', label: 'Production deleted', module: 'PRODUCTIONS' },
  { value: 'ALIGNMENT_UPSERT', label: 'Alignment saved', module: 'PRODUCTIONS' },
  { value: 'FITMENT_UPSERT', label: 'Fitment saved', module: 'PRODUCTIONS' },
  { value: 'MATERIAL_CONSUMPTION_BULK_UPSERT', label: 'Material consumption saved', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_SHEET_UPSERT', label: 'Production sheet saved', module: 'PRODUCTIONS' },
  { value: 'PIPE_DOCUMENT_APPROVAL_UPDATED', label: 'Pipe document approval updated', module: 'PIPE_DOCUMENTS' },
  { value: 'COATING_PUMPHOUSE_BULK_UPSERT', label: 'Coating pumphouse saved', module: 'COATING' },
  { value: 'COATING_PUMPHOUSE_DELETED', label: 'Coating pumphouse deleted', module: 'COATING' },
  { value: 'PUMPHOUSE_LIMIT_CONFIG_UPDATED', label: 'Pumphouse limit config updated', module: 'COATING' },
  { value: 'CUSTOMER_DELETED', label: 'Customer deleted', module: 'CUSTOMER' },
  { value: 'CONTRACTOR_DELETED', label: 'Contractor deleted', module: 'CONTRACTOR' },
  { value: 'MANUFACTURER_DELETED', label: 'Manufacturer deleted', module: 'MANUFACTURER' },
  { value: 'TAG_UPDATED', label: 'Tag updated', module: 'TAGS' },
  { value: 'TAG_BULK_UPDATED', label: 'Tags bulk updated', module: 'TAGS' },
  { value: 'TAG_DELETED', label: 'Tag deleted', module: 'TAGS' },
  { value: 'PRODUCTION_WELD_NUMBER_UPDATED', label: 'Weld number updated', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_JOB_NUMBER_RENAMED', label: 'Job number renamed', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_WELD_PASSES_MERGED', label: 'Weld passes merged', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_WELD_PASSES_UNMERGED', label: 'Weld passes unmerged', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_WELD_PARAMETER_PASS_RENAMED', label: 'Weld parameter pass renamed', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_STATUS_CONFIGURATION_UPDATED', label: 'Status configuration updated', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_REPORT_GENERATED', label: 'Production report generated', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_PQR_REPORT_GENERATED', label: 'PQR report generated', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_MODAL_OPENED', label: 'Production modal opened', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_WELD_LOG_UPLOADED', label: 'Weld log uploaded', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_PIPE_ASSIGNED_TO_WELD', label: 'Pipe assigned to weld', module: 'PRODUCTIONS' },
  {
    value: 'PRODUCTION_WELD_ITEMS_ASSIGNED_TO_PROJECT',
    label: 'Weld items assigned to project',
    module: 'PRODUCTIONS',
  },
  { value: 'PRODUCTION_REPORT_COLUMNS_UPDATED', label: 'Report columns updated', module: 'PRODUCTIONS' },
  { value: 'PRODUCTION_IRN_REPORT_GENERATED', label: 'IRN report generated', module: 'PRODUCTIONS' },
  { value: 'SPOOL_NCR_REGISTERED', label: 'NCR registered', module: 'SPOOL' },
  { value: 'SPOOL_BASE_CREATED', label: 'Spool created', module: 'SPOOL' },
  { value: 'SPOOL_BASE_UPDATED', label: 'Spool updated', module: 'SPOOL' },
  { value: 'SPOOL_BASE_DELETED', label: 'Spool deleted', module: 'SPOOL' },
  { value: 'REPORT_GENERATED', label: 'Report generated', module: 'REPORTS' },
  { value: 'REPORT_DRAFT_SAVED', label: 'Report draft saved', module: 'REPORTS' },
  { value: 'REPORT_DOCUMENT_STORED', label: 'Report document stored', module: 'REPORTS' },
  { value: 'DEVICE_PROJECT_ASSIGNED', label: 'Device project assigned', module: 'DEVICE' },
  { value: 'DEVICE_PROJECT_CHANGED', label: 'Device project changed', module: 'DEVICE' },
  { value: 'DEVICE_PROJECT_BULK_ASSIGNED', label: 'Devices assigned to project', module: 'DEVICE' },
  { value: 'DEVICE_WELD_PARAMS_DOWNLOAD_STARTED', label: 'Weld parameters download started', module: 'DEVICE' },
  { value: 'DEVICE_LOGS_ERASE_STARTED', label: 'Device logs erase started', module: 'DEVICE' },
  { value: 'DEVICE_TIME_SYNC_STARTED', label: 'Device time sync started', module: 'DEVICE' },
  { value: 'DEVICE_GET_TIME_REQUESTED', label: 'Device time read requested', module: 'DEVICE' },
  { value: 'DEVICE_FIRMWARE_UPLOADED', label: 'Device firmware uploaded', module: 'DEVICE' },
  { value: 'DEVICE_FIRMWARE_UPDATE_STARTED', label: 'Device firmware update started', module: 'DEVICE' },
  { value: 'DEVICE_REGISTRATION_OPENED', label: 'Device registration opened', module: 'DEVICE' },
  { value: 'DEVICE_STATUS_UPDATED', label: 'Device status updated', module: 'DEVICE' },
  { value: 'OPTIFIT_DEVICE_REGISTERED', label: 'Optifit device registered', module: 'DEVICE' },
  { value: 'OPTIFIT_DEVICE_UPDATED', label: 'Optifit device updated', module: 'DEVICE' },
  { value: 'OPTIFIT_DEVICE_STATUS_CHANGED', label: 'Optifit device status changed', module: 'DEVICE' },
  { value: 'OPTIFIT_DEVICE_PASSWORD_RESET', label: 'Optifit device password reset', module: 'DEVICE' },
  { value: 'DOMAIN_DATA_MUTATION', label: 'Data mutation' },
];

export const EVENT_LABELS: Record<string, string> = Object.fromEntries(
  AUDIT_EVENTS.map((e) => [e.value, e.label])
);

const GENERIC_ACTIVITY_LABELS = new Set([
  'activity page view',
  'page opened',
  'page view',
  'activity tab view',
  'tab opened',
  'tab view',
  'production modal opened',
]);

function parseActionDetailsObject(
  actionDetails: AuditTrailItem['action_details']
): Record<string, unknown> | null {
  if (!actionDetails) return null;
  if (typeof actionDetails === 'object' && !Array.isArray(actionDetails)) {
    return actionDetails as Record<string, unknown>;
  }
  if (typeof actionDetails === 'string') {
    try {
      const parsed = JSON.parse(actionDetails);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

function readActionDetailString(
  details: Record<string, unknown> | null,
  key: string
): string | null {
  const value = details?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function formatAuditProjectSuffix(
  row: Pick<AuditTrailItem, 'project_name' | 'project_id' | 'action_details'>
): string {
  const name = row.project_name?.trim();
  if (name) return ` in ${name}`;

  const details = parseActionDetailsObject(row.action_details);
  const nameFromDetails = readActionDetailString(details, 'projectName');
  if (nameFromDetails) return ` in ${nameFromDetails}`;

  const idFromDetails = details?.projectId ?? details?.project_id;
  if (idFromDetails != null && idFromDetails !== '') return ` in project ${idFromDetails}`;

  if (row.project_id != null) return ` in project ${row.project_id}`;
  return '';
}

function appendProjectToSummary(
  summary: string,
  row: Pick<AuditTrailItem, 'project_name' | 'project_id' | 'action_details'>
): string {
  const suffix = formatAuditProjectSuffix(row);
  if (!suffix) return summary;
  if (summary.includes(suffix.trim())) return summary;
  const trimmed = summary.trim();
  if (!trimmed) return `${suffix.trim().slice(3)}.`;
  if (trimmed.endsWith('.')) return `${trimmed.slice(0, -1)}${suffix}.`;
  return `${trimmed}${suffix}.`;
}

function isGenericActivityLabel(value: string | null | undefined): boolean {
  if (!value?.trim()) return false;
  return GENERIC_ACTIVITY_LABELS.has(value.trim().toLowerCase());
}

/** Resolve a human page/modal/tab name from stored audit row (incl. legacy generic rows). */
export function getActivityContextName(
  row: Pick<AuditTrailItem, 'event_type' | 'entity_name' | 'display_title' | 'action_details'>
): string | null {
  const details = parseActionDetailsObject(row.action_details);
  const fromDetails =
    readActionDetailString(details, 'page') ??
    readActionDetailString(details, 'modal') ??
    readActionDetailString(details, 'tab');

  const candidates = [
    fromDetails,
    row.display_title?.trim() || null,
    row.entity_name?.trim() || null,
  ].filter((value): value is string => Boolean(value));

  for (const candidate of candidates) {
    if (/^page-/i.test(candidate) || /^tab-/i.test(candidate)) continue;
    if (isGenericActivityLabel(candidate)) continue;
    return candidate;
  }

  return fromDetails;
}

export function getActivityEntityDisplayName(
  row: Pick<AuditTrailItem, 'event_type' | 'entity_name' | 'display_title' | 'action_details'>
): string | null {
  return getActivityContextName(row);
}

export function getModuleLabel(module: string | null | undefined): string {
  if (!module) return '—';
  return MODULE_LABELS[module] ?? module.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getEventLabel(eventType: string | null | undefined): string {
  if (!eventType) return '—';
  return (
    EVENT_LABELS[eventType] ??
    eventType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function getEventDisplayLabel(
  row: Pick<AuditTrailItem, 'event_type' | 'event_label' | 'entity_name' | 'display_title' | 'action_details'>
): string {
  const activityName = getActivityContextName(row);
  if (activityName) {
    if (row.event_type === 'PRODUCTION_MODAL_OPENED') return activityName;
    if (row.event_type?.startsWith('ACTIVITY_')) return activityName;
  }

  const label = row.event_label?.trim();
  if (label && !isGenericActivityLabel(label)) return label;
  return getEventLabel(row.event_type);
}

export function getAuditSummary(
  row: Pick<
    AuditTrailItem,
    | 'summary'
    | 'display_title'
    | 'event_type'
    | 'event_label'
    | 'entity_name'
    | 'action_details'
    | 'project_name'
    | 'project_id'
  >
): string {
  const activityName = getActivityContextName(row);
  if (activityName) {
    if (row.event_type === 'ACTIVITY_PAGE_VIEW') {
      return appendProjectToSummary(`Opened ${activityName}.`, row);
    }
    if (row.event_type === 'ACTIVITY_TAB_VIEW') {
      return appendProjectToSummary(`Opened ${activityName} tab.`, row);
    }
    if (row.event_type === 'PRODUCTION_MODAL_OPENED') {
      return appendProjectToSummary(`Opened ${activityName} modal.`, row);
    }
  }

  const summary = row.summary?.trim();
  if (summary && !isGenericActivityLabel(summary.replace(/\.$/, ''))) {
    return appendProjectToSummary(summary, row);
  }
  const title = row.display_title?.trim();
  if (title && !isGenericActivityLabel(title) && !/^page-|^tab-/i.test(title)) {
    return appendProjectToSummary(title, row);
  }
  return appendProjectToSummary(getEventDisplayLabel(row), row);
}

export function getTimelineSummary(
  row: Pick<
    AuditTrailItem,
    | 'summary'
    | 'display_title'
    | 'event_type'
    | 'event_label'
    | 'entity_name'
    | 'module'
    | 'action_details'
    | 'project_name'
    | 'project_id'
  >
): string {
  const stripIdNoise = (text: string) =>
    text.replace(/\s*\(ID[^)]*\)/gi, '').replace(/\s+/g, ' ').trim();

  return stripIdNoise(getAuditSummary(row));
}

const TIMELINE_EVENT_BADGES: Record<string, string> = {
  ACTIVITY_TAB_VIEW: 'TAB VIEW',
  ACTIVITY_PAGE_VIEW: 'PAGE VIEW',
  ACTIVITY_SEARCH: 'SEARCH',
  ACTIVITY_FILTER_APPLIED: 'FILTER APPLIED',
  ACTIVITY_FILTER_CLEARED: 'FILTER CLEARED',
  ACTIVITY_EXPORT: 'EXPORT',
  AUTH_LOGIN_SUCCESS: 'LOGIN SUCCESS',
  AUTH_LOGIN_FAILED: 'LOGIN FAILED',
  AUTH_LOGOUT: 'LOGOUT',
  ACCESS_DENIED: 'ACCESS DENIED',
};

/** Uppercase badge label for timeline (mockup tl-type). */
export function getTimelineEventBadge(eventType: string | null | undefined): string {
  if (!eventType) return 'EVENT';
  if (TIMELINE_EVENT_BADGES[eventType]) return TIMELINE_EVENT_BADGES[eventType];
  if (eventType === 'PRODUCTION_MODAL_OPENED') return 'MODAL OPENED';
  return eventType.replace(/_/g, ' ');
}

const TIMELINE_MODULE_SHORT: Record<string, string> = {
  ACTIVITY: 'Activity',
  COGNITO: 'Authentication',
  SECURITY: 'Security',
};

/** Short module label for timeline meta line. */
export function getTimelineMetaModule(module: string | null | undefined): string {
  if (!module) return 'System';
  return TIMELINE_MODULE_SHORT[module] ?? getModuleLabel(module);
}

export function getProjectDisplayName(
  row: Pick<AuditTrailItem, 'project_name' | 'project_id'>
): string {
  const name = row.project_name?.trim();
  if (name) return name;
  if (row.project_id != null) return `Project #${row.project_id}`;
  return '—';
}

/** Table cell: project name + summary (no project number). */
export function getProjectSummaryDisplay(
  row: Pick<
    AuditTrailItem,
    | 'project_name'
    | 'project_id'
    | 'summary'
    | 'display_title'
    | 'event_type'
    | 'event_label'
    | 'entity_name'
    | 'action_details'
  >,
  options?: { showProjectName?: boolean }
): { primary: string; secondary: string } {
  const showProject = options?.showProjectName !== false;
  const summary = getAuditSummary(row);
  if (!showProject) {
    const projectName = row.project_name?.trim() || formatAuditProjectSuffix(row).replace(/^ in /, '');
    return {
      primary: summary,
      secondary: projectName && !summary.includes(projectName) ? projectName : '',
    };
  }
  return {
    primary: getProjectDisplayName(row),
    secondary: summary,
  };
}

export function getSeverityLabel(severity: string | null | undefined): string {
  if (!severity) return '—';
  return SEVERITY_LABELS[severity] ?? severity;
}

export function getEventsForModule(module: string | undefined): typeof AUDIT_EVENTS {
  if (!module) return AUDIT_EVENTS;
  return AUDIT_EVENTS.filter((e) => !e.module || e.module === module);
}

export function parseActionDetails(
  raw: string | Record<string, unknown> | null | undefined
): {
  parsed: unknown | null;
  display: string;
  isJson: boolean;
} {
  if (raw == null || raw === '') {
    return { parsed: null, display: '—', isJson: false };
  }
  if (typeof raw === 'object') {
    return { parsed: raw, display: JSON.stringify(raw, null, 2), isJson: true };
  }
  try {
    const parsed = JSON.parse(raw);
    return { parsed, display: JSON.stringify(parsed, null, 2), isJson: true };
  } catch {
    return { parsed: null, display: raw, isJson: false };
  }
}


export function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

export function getActorDisplay(row: Pick<AuditTrailItem, 'actor_name' | 'actor_email' | 'actor_id'>): {
  primary: string;
  secondary?: string;
} {
  const email = row.actor_email?.trim() || '';
  const rawId = row.actor_id?.trim() || '';
  const name = row.actor_name?.trim() || '';

  const idIsEmail = rawId.includes('@');
  const resolvedEmail = email || (idIsEmail ? rawId : '');

  if (name && !looksLikeUuid(name) && name !== resolvedEmail) {
    return { primary: name, secondary: resolvedEmail || undefined };
  }

  if (resolvedEmail) {
    return { primary: resolvedEmail, secondary: name && !looksLikeUuid(name) ? name : undefined };
  }

  if (name && !looksLikeUuid(name)) {
    return { primary: name };
  }

  return { primary: rawId || name || '—' };
}

/** Best single-line actor label (prefers email/name over Cognito UUID). */
export function getActorLabel(
  row: Pick<AuditTrailItem, 'actor_name' | 'actor_email' | 'actor_id'>
): string {
  return getActorDisplay(row).primary;
}
